import { DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ServicecallService } from 'src/app';
import { ApiConstants } from 'src/app/constants/api-constants';
import { AttributeValues, SummaryDetails, TripItem, TripSummaryDetails } from 'src/app/model';
import { EntitiesServiceService } from 'src/app/shared/services';

export interface TripSegmentData {
  id: string;
  route: {
    from: string;
    to: string;
    startTime: string;
    endTime: string;
  };
  battery: {
    startRange: number;
    currentRange: number;
    lowBatteryWarning?: boolean;
  };
  metrics: {
    distance: number;
    duration: string;
    avgSpeed: number;
    efficiency: number;
  };
  insights: {
    ecoPerformance?: string;
    routeOptimization?: string;
  };
  selectedTripDetails: TripItem;
  summaryData: SummaryDetails;
}
export interface StatusCard {
  id: number;
  value: string | number;
  label: string;
  type: 'brake' | 'temperature';
  unit?: string;
  attribute : string;
}
@Component({
  selector: 'app-trip-segment-popover',
  templateUrl: './trip-segment-popover.component.html',
  styleUrl: './trip-segment-popover.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class TripSegmentPopoverComponent implements OnInit, OnDestroy {
  segmentLocation: string;
  tripDetails : TripSummaryDetails;
  distanceTravelled: number;
  timeTaken: number;
  tripHeartbeatAttributes: AttributeValues[] = [];
  startingSoc : number;
  batteryState: string ;
  token: any = ApiConstants.blobSasToken;

  //Subscriptions
  private _getTripDetailsSubscription: Subscription;
  private _trackEntitySubscription: Subscription;

  //Attribute details
  statusData: StatusCard[] = [
    { id: 1, value: null, label: 'Harsh Brakes', type: 'brake', attribute: "Harsh Brakes" },
    { id: 2, value: null, label: 'Harsh Acceleration', type: 'brake', attribute: "Harsh Acceleration" },
    { id: 3, value: null, label: 'Overspeed', type: 'brake', attribute: "Overspeed" },
    { id: 4, value: null, label: 'Idle time', type: 'brake', attribute: "Idle time" },
    { id: 5, value: null, label: 'Battery Temp', type: 'temperature', unit: '°C', attribute: "battery_temp" },
    { id: 6, value: null, label: 'Motor Temp', type: 'temperature', unit: '°C' , attribute: "motor_temp"},
    { id: 7, value: null, label: 'Inverter Temp', type: 'temperature', unit: '°C' , attribute: "inverter_temp"},
    { id: 8, value: null, label: 'Ambient Temp', type: 'temperature', unit: '°C' , attribute: "ambient_temp"},
  ];

  optimizationTipsAttributes : {attrName: string, name: string, icon?: string}[] = [
    {attrName: "Speed", name: "speed", icon : null},
    {attrName: "Acceleration", name: "acceleration", icon : null},
    {attrName: "HVAC", name: "hvac", icon : null},
  ]
  
  constructor(
    public dialogRef: MatDialogRef<TripSegmentPopoverComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TripSegmentData,
    private _apiService: EntitiesServiceService,
    private _sharedService : ServicecallService,
    private _datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    // Component initialization logic
    this.statusData = this._sharedService.getConfiguration('tenantGenericConfig')?.prescriptiveInsights?.statusData ?? [];
    this.optimizationTipsAttributes = this._sharedService.getConfiguration('tenantGenericConfig')?.prescriptiveInsights?.segmentOptimizationTripsAttributes ?? [];
    this.getTripSegmentSummary();
    this.getTripHeartbeatData();
    this.getStartingSoc();
    if(this.data?.selectedTripDetails?.currentLocation?.[0] && this.data?.selectedTripDetails?.currentLocation?.[1]) {
      this._getLocationFromCoordinates(this.data?.selectedTripDetails?.currentLocation?.[0],this.data?.selectedTripDetails?.currentLocation?.[1])
    }
  }

  ngOnDestroy() : any {
    this._getTripDetailsSubscription?.unsubscribe();
    this._trackEntitySubscription?.unsubscribe();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onGoToStation(): void {
    // Handle charging station navigation
    console.log('Navigate to charging station');
  }

  trackByIndex(index: number): number {
    return index;
  }

    /**
   * Gets location from coordinates
   * @param latitude
   * @param longitude
   * @param locationType
   * @returns location from coordinates
   */
     public async _getLocationFromCoordinates(
      latitude: number,
      longitude: number
    ): Promise<any> {
      try {
        const response = await fetch(
          `${ApiConstants?.HERE_REVERSE_GEOCODE_URL}?at=${latitude},${longitude}&apiKey=${ApiConstants?.HERE_API_KEY}&lang=en-US`
        );
        const data = await response?.json();
        this.segmentLocation = data?.items?.[0]?.address?.label;
      } catch (error) {
        console.error('Error in reverse geocoding:', error);
        this.segmentLocation = null;
      }
    }


    /**
     * Gets trip segment summary
     */
    getTripSegmentSummary() {
      const _reqPayload = {
        entityId: this.data?.summaryData?.entityId,
        siteId: this._sharedService.getSiteId(),
        from: this._datePipe.transform(
          this.data?.selectedTripDetails?.dataFrom,
          'yyyy-MM-ddTHH:mm:ssZZ'
        ),
        to: this._datePipe.transform(
          this.data?.selectedTripDetails?.dataTo,
          'yyyy-MM-ddTHH:mm:ssZZ'
        ),
        isMock: false,
      };

      this._getTripDetailsSubscription = this._apiService
      .getEntitySummary(_reqPayload)
      .subscribe({
        next: (result: any) => {
          this.tripDetails = result;
          this.timeTaken = this._sharedService.getConfiguration('tenantGenericConfig')?.timeTakenIn === 'hr' ? (this.tripDetails?.timeTakenInMin * 3600) : (this.tripDetails?.timeTakenInMin * 60); // Converts hr/minute to second
          this.distanceTravelled = this.tripDetails?.distanceTravelled; 
        },
        error: () => {
          this.tripDetails = null;
        },
      });
    }

  trackByFn(index: number, item: StatusCard): number {
    return item.id;
  }

  /**
   * Gets starting soc from heartbeat data
   */
  getStartingSoc() {
    const payload = {
      siteId: this._sharedService.getSiteId(),
      entityId: this.data?.summaryData?.entityId,
      to : this._datePipe.transform(this.data?.selectedTripDetails?.dataFrom, 'yyyy-MM-ddTHH:mm:ssZZ')
    };
    this._trackEntitySubscription = this._sharedService.trackEntity(payload, 1, true).subscribe({
      next: (result: any) => {
        if (result['status']) {
          if (result['response']['data']?.length) {
            const tableData = [...result['response']['data']]?.find((item: any) => Object.keys(item)?.includes('original'));
            this.startingSoc = tableData?.original ? (tableData.original?.[0]?.attributes?.find(item => item.attributeName === 'battery_soc')?.attributeValue ? 
            Number(Number(tableData.original?.[0]?.attributes?.find(item => item.attributeName === 'battery_soc')?.attributeValue)?.toFixed(2)) : null) : null;
          } else {
            this.startingSoc = null;
          }
        }
        else {
          this.startingSoc = null;
        }
      },
      error: (error) => {
        this.startingSoc = null;
        this._sharedService?.toasterService({ type: 'error', msg: error?.message })
      }
    });
  }

  /**
   * Gets trip heartbeat data
   */
  getTripHeartbeatData() {
    const payload = {
      siteId: this._sharedService.getSiteId(),
      entityId: this.data?.summaryData?.entityId,
      to : this._datePipe.transform(this.data?.selectedTripDetails?.dataTo, 'yyyy-MM-ddTHH:mm:ssZZ')
    };
    this._trackEntitySubscription = this._sharedService.trackEntity(payload, 1, true).subscribe({
      next: (result: any) => {
        if (result['status']) {
          if (result['response']['data']?.length) {
              const tableData = [...(result?.response?.data ?? [])].find((item: any) => Object.keys(item ?? {})?.includes('original'));
              const originalAttributes = tableData?.original?.[0]?.attributes;
              this.tripHeartbeatAttributes = Array.isArray(originalAttributes) ? originalAttributes : [];
              const currentSoc = Number(this.getAttributeValue('battery_soc')) ?? 0;
              if(currentSoc < 30) {
                this.batteryState = 'critical';
              } else if(currentSoc >=30 && currentSoc <=60) {
                this.batteryState = 'low';
              } else {
                this.batteryState = 'full';
              }
              if (Array.isArray(this.statusData)) {
                this.statusData?.forEach((statusItem) => {
                  const matchedAttribute = this.tripHeartbeatAttributes?.find(
                    (attr) => attr?.attributeName?.trim()?.toLowerCase() === statusItem?.attribute?.trim()?.toLowerCase()
                  );
                  if(matchedAttribute) {
                    statusItem.value = Number(matchedAttribute?.attributeValue)?.toFixed(1) ?? null;
                  }
                });
                this.getAlertsCount();
              }
            } else {
              this.batteryState = null;
              this.tripHeartbeatAttributes = [];
              this.getAlertsCount();
            }
          } else {
            this.batteryState = null;
            this.tripHeartbeatAttributes = [];
            this.getAlertsCount();
        }
      },
      error: (error) => {
        this.tripHeartbeatAttributes = [];
        this.getAlertsCount();
        this._sharedService?.toasterService({ type: 'error', msg: error?.message })
      }
    });
  }

  /**
   * Gets attribute value
   * @param attrName 
   * @returns  
   */
  getAttributeValue(attrName: string) {
    return Number(this.tripHeartbeatAttributes?.find(item => item.attributeName === attrName)?.attributeValue)?.toFixed(2);
  }

  /**
   * Rounds offto decmal
   * @param value 
   * @returns  
   */
  roundOfftoDecmal(value: string) {
    return Number(value)?.toFixed(2);
  }


  /**
   * Gets alerts count
   */
  getAlertsCount() {
    const reqData = {
      from: this._datePipe.transform(this.data?.selectedTripDetails?.dataFrom, 'yyyy-MM-ddTHH:mm:ssZZ'),
      to: this._datePipe.transform(this.data?.selectedTripDetails?.dataTo, 'yyyy-MM-ddTHH:mm:ssZZ'),
      siteId: this._sharedService.getSiteId(),
      filterBy: "Alert",
      status: true,
      classType: "Alert",
      entityId: this.data?.summaryData?.entityId,
    };
    this._sharedService.getAlertsCount(reqData, false).subscribe((result: any) => {
      if (result['status']) {
        const alert = result['response'] ? result['response']['data']?.find((item: any) => item.alert)?.alert : null;
        this.statusData?.forEach((statusItem)=> {
          const matchedAlert = alert?.find(item => item.alert === statusItem.attribute);
          if(matchedAlert) {
            statusItem.value = matchedAlert?.count;
          }
        })
      }
      else {
        this._sharedService.toasterService({ type: 'error', msg: result?.response?.message });
      }
    })
  }

  /**
   * Gets optimization attributes name
   * @param attr 
   * @returns  
   */
  getOptimizationAttributesName(attr: string) {
    return this.optimizationTipsAttributes?.find(item => item.name === attr);
  }
}
